import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import RevolutCheckout from "@revolut/checkout";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

export default function CardInvite() {
  const { token } = useParams();
  const host = useRef(null);
  const field = useRef(null);
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [name, setName] = useState("");
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/revolut/card-invite/${token}`).then(async ({ data }) => {
      if (dead) return;
      setInfo(data); setState("ready");
      if (!data.token) { setState("nowidget"); return; }
      try {
        const rc = await RevolutCheckout(data.token, data.mode === "production" ? "prod" : "sandbox");
        if (dead || !host.current) return;
        field.current = rc.createCardField({
          target: host.current,
          savePaymentMethodFor: "merchant",
          onValidation: (errs) => setReady((errs || []).length === 0),
          onSuccess: () => { toast.success("Carte enregistrée — abonnement activé !"); setState("done"); },
          onError: (e) => toast.error(e?.message || "Échec du paiement"),
        });
      } catch { setState("nowidget"); }
    }).catch(() => setState("invalid"));
    return () => { dead = true; try { field.current?.destroy(); } catch { /* noop */ } };
  }, [token]);

  const submit = () => {
    if (!name.trim()) return toast.error("Indiquez le nom du titulaire");
    setSubmitting(true);
    field.current?.submit({ name, savePaymentMethodFor: "merchant" });
    setTimeout(() => setSubmitting(false), 4000);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-2xl" data-testid="card-invite">
        <img src="/Logo_Full_Black.webp" alt="HalgarOS" className="mb-6 h-9 w-auto dark:hidden" />
        <img src="/Logo_Full_White.webp" alt="HalgarOS" className="mb-6 hidden h-9 w-auto dark:block" />
        {state === "loading" && <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Chargement…</p>}
        {state === "invalid" && <p data-testid="card-invalid" className="text-sm text-muted-foreground">Cette invitation est invalide ou a expiré. Contactez HalgarOS.</p>}
        {state === "done" && (
          <motion.div data-testid="card-done" className="py-6 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15"
              initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}>
              <CheckCircle2 className="h-9 w-9 text-emerald-500" strokeWidth={2} />
            </motion.div>
            <h1 className="mt-4 font-display text-xl font-bold text-foreground">Abonnement activé 🎉</h1>
            <p className="mt-1 text-sm text-muted-foreground">Merci ! Vous pouvez retourner sur HalgarOS, tout est débloqué.</p>
          </motion.div>
        )}
        {(state === "ready" || state === "nowidget") && info && (
          <>
            <h1 className="font-display text-2xl font-bold text-foreground">Activez votre abonnement</h1>
            <p className="mt-1 text-sm text-muted-foreground">{info.org_name} · <strong className="text-foreground">{info.amount} € / mois</strong></p>
            {state === "nowidget" ? (
              <p className="mt-6 text-sm text-muted-foreground">Le formulaire de carte n'est pas disponible pour le moment. Merci de réessayer plus tard.</p>
            ) : (
              <div className="mt-6 space-y-4">
                <div><Label className="text-muted-foreground">Nom du titulaire</Label><Input data-testid="card-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
                <div><Label className="text-muted-foreground">Carte bancaire</Label><div ref={host} data-testid="card-field" className="mt-1.5 rounded-md border border-border bg-muted p-3" /></div>
                <Button data-testid="card-submit" onClick={submit} disabled={!ready || submitting} className="w-full rounded-full bg-[#FF6A00] py-6 font-semibold text-white hover:bg-[#E65F00]">Enregistrer ma carte</Button>
                <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" />Paiement sécurisé par Revolut — HalgarOS ne voit jamais votre carte.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
